import { useEffect, useState } from "react";
import { toast } from "sonner";
import moment from "moment";
import { Search } from "lucide-react";
import { getInvoices } from "../../api/api";
import DataTable from "../../components/DataTable";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const TABS = [
  { key: "all", label: "All", status: null },
  { key: "unpaid", label: "Unpaid", status: "unpaid" },
  { key: "partial", label: "Partial", status: "partial" },
  { key: "paid", label: "Paid", status: "paid" },
];

const STATUS_VARIANT = { unpaid: "destructive", partial: "warning", paid: "secondary" };

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });

  useEffect(() => {
    setLoading(true);
    const status = TABS.find((t) => t.key === tab)?.status;
    getInvoices({ status: status || undefined, search: search || undefined, page, limit: 15 })
      .then((res) => {
        setInvoices(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load invoices."))
      .finally(() => setLoading(false));
  }, [tab, search, page]);

  const columns = [
    { key: "invoice_no", header: "Invoice #" },
    {
      key: "patient",
      header: "Patient",
      render: (i) => (
        <div>
          <p className="font-medium">{i.patient_id?.name}</p>
          <p className="text-xs text-muted-foreground">{i.patient_id?.phone}</p>
        </div>
      ),
    },
    { key: "doctor", header: "Doctor", render: (i) => i.doctor_id?.name },
    { key: "date", header: "Date", render: (i) => moment(i.createdAt).format("D MMM YYYY") },
    { key: "total_amount", header: "Amount", render: (i) => `₹${i.total_amount.toFixed(2)}` },
    { key: "paid_amount", header: "Paid", render: (i) => `₹${i.paid_amount.toFixed(2)}` },
    { key: "status", header: "Status", render: (i) => <Badge variant={STATUS_VARIANT[i.status]}>{i.status}</Badge> },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-serif text-2xl font-semibold">Billing & Invoices</h2>
        <p className="text-sm text-muted-foreground">Every invoice generated from the Appointments desk</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }}>
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by patient or phone…"
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={invoices}
        loading={loading}
        emptyMessage="No invoices generated yet."
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(i) => i._id}
      />
    </div>
  );
}
